import React from "react";
import { formatDate } from "../../utils/dateUtils";
import { PictureInPictureVideo } from "../../../swing_library/components/Table/PictureInPictureVideo";
import VideoActionMenu from "./VideoActionMenu";
import { MdArrowDropDown, MdArrowDropUp } from "react-icons/md";

/**
 * Extracts cell value from video object based on column key
 * @param {Object} video - Video object
 * @param {string} columnKey - Column key identifier
 * @returns {string} Extracted value
 */
function formatTags(tags) {
  if (!Array.isArray(tags) || tags.length === 0) return "—";
  return tags.join(", ");
}

export function extractCellValue(video, columnKey) {
  switch (columnKey) {
    case "id":
      return video.id || "—";
    case "parentUserId":
      return video.parentUserId || "—";
    case "parentSessionId":
      return video.parentSessionId || "—";
    case "videoOrigName":
      return video.videoOrigName || "—";
    case "videoCreated":
      return video.videoCreated ? formatDate(video.videoCreated) : "—";
    case "videoLength":
      return video.videoLength != null ? String(video.videoLength) : "—";
    case "videoType":
      return video.videoType || "—";
    case "videoSource":
      return video.videoSource || "—";
    case "cameraAngle":
      return video.metaData?.cameraAngle || "—";
    case "golfClub":
      return video.userData?.golfClub || "—";
    case "fullName":
      return video.userData?.fullName || "—";
    case "score":
      return video.score != null ? String(video.score) : "—";
    case "analyzed":
      return video.analyzed != null ? (video.analyzed ? "Yes" : "No") : "—";
    case "videoSize":
      return video.videoSize != null ? String(video.videoSize) : "—";
    case "videoIsSplit":
      return video.videoIsSplit != null
        ? video.videoIsSplit
          ? "Yes"
          : "No"
        : "—";
    case "favorite":
      return video.favorite != null ? (video.favorite ? "Yes" : "No") : "—";
    case "goalId":
      return video.goalId || "—";
    case "analysisPlatform":
      return video.analysisPlatform || "—";
    case "tags":
      return formatTags(video.tags);
    case "actionIdsCount":
      return Array.isArray(video.actionIds)
        ? String(video.actionIds.length)
        : "0";
    case "age":
      return video.userData?.age != null ? String(video.userData.age) : "—";
    case "gender":
      return video.userData?.gender || "—";
    case "hand":
      return video.userData?.hand || "—";
    case "height":
      return video.userData?.height || "—";
    case "heightInInches":
      return video.userData?.heightInInches != null
        ? String(video.userData.heightInInches)
        : "—";
    case "skillLevel":
      return video.userData?.skillLevel ?? "—";
    case "hipMeasurement":
      return video.userData?.hipMeasurement ?? "—";
    case "comment":
      return video.userData?.comment ?? "—";
    case "resolutions":
      return video.metaData?.resolutions || "—";
    case "fps":
      return video.metaData?.fps != null ? String(video.metaData.fps) : "—";
    case "frameCount":
      return video.metaData?.frameCount != null
        ? String(video.metaData.frameCount)
        : "—";
    case "metaDataName":
      return video.metaData?.name || "—";
    case "metaDataBrand":
      return video.metaData?.brand ?? "—";
    case "metaDataModel":
      return video.metaData?.model ?? "—";
    default:
      return "—";
  }
}

function SortIcon({ direction }) {
  const Icon = direction === "desc" ? MdArrowDropDown : MdArrowDropUp;
  return <Icon className="size-5 min-w-5" />;
}

/**
 * Creates column definitions for the admin search videos table
 * @param {Object} [options] - Optional sort state and handler for sortable columns
 * @param {string} [options.sort] - Current sort field (e.g. "videoCreated")
 * @param {string} [options.order] - "asc" | "desc"
 * @param {function(string)} [options.onSort] - Called with column key when sortable header is clicked
 * @returns {Object} Column definitions object
 */
export function createVideosColumnDefinitions(options = {}) {
  const { sort, order, onSort } = options;

  const renderHeader = (label) => <th className="px-2 py-3">{label}</th>;

  const renderSortableHeader = (label, columnKey) => {
    if (!onSort || typeof onSort !== "function") {
      return renderHeader(label);
    }
    const isActive = sort === columnKey;
    const dir = isActive ? order : null;
    return (
      <th className="px-2 py-3">
        <button
          type="button"
          onClick={() => onSort(columnKey)}
          className="inline-flex cursor-pointer select-none items-center gap-1 rounded text-left font-medium uppercase tracking-wide text-gray600 outline-none hover:text-gray900 focus:ring-2 focus:ring-primary300"
        >
          {label}
          <SortIcon direction={dir} />
        </button>
      </th>
    );
  };

  return {
    actions: {
      key: "actions",
      label: "Actions",
      renderHeader: () => <th className="px-0.5 py-3 text-center"></th>,
      renderCell: (video, handlers) => (
        <td className="px-0.5 py-3 text-center">
          <VideoActionMenu
            onDownload={() => handlers?.onDownloadVideo?.(video)}
          />
        </td>
      ),
    },
    videoCreated: {
      key: "videoCreated",
      label: "Video Created",
      renderHeader: () => renderSortableHeader("Video Created", "videoCreated"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoCreated")}</td>
      ),
    },
    videoPath: {
      key: "videoPath",
      label: "Video Path",
      renderHeader: () => renderHeader("Video Path"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          <div className="w-fit">
            <PictureInPictureVideo videoPath={video.videoPath} />
          </div>
        </td>
      ),
    },
    thumbnail: {
      key: "thumbnail",
      label: "Thumbnail",
      renderHeader: () => renderHeader("Thumbnail"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          <div className="w-fit">
            {video.thumbnailPath ? (
              <a
                href={video.videoPath}
                target="_blank"
                rel="noopener noreferrer"
                className="block size-10 overflow-hidden rounded border border-gray200"
              >
                <img
                  src={video.thumbnailPath}
                  alt=""
                  className="size-10 object-cover"
                />
              </a>
            ) : (
              <span className="text-gray400">—</span>
            )}
          </div>
        </td>
      ),
    },
    id: {
      key: "id",
      label: "ID",
      renderHeader: () => renderHeader("ID"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          <span className="font-mono text-sm text-gray700">
            {extractCellValue(video, "id")}
          </span>
        </td>
      ),
    },
    parentUserId: {
      key: "parentUserId",
      label: "Parent User ID",
      renderHeader: () => renderHeader("Parent User ID"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          <span className="font-mono text-sm">
            {extractCellValue(video, "parentUserId")}
          </span>
        </td>
      ),
    },
    parentSessionId: {
      key: "parentSessionId",
      label: "Parent Session ID",
      renderHeader: () => renderHeader("Parent Session ID"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          <span className="font-mono text-sm">
            {extractCellValue(video, "parentSessionId")}
          </span>
        </td>
      ),
    },

    videoLength: {
      key: "videoLength",
      label: "Length (ms)",
      renderHeader: () => renderHeader("Length (ms)"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoLength")}</td>
      ),
    },
    videoType: {
      key: "videoType",
      label: "Video Type",
      renderHeader: () => renderHeader("Video Type"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoType")}</td>
      ),
    },
    videoSource: {
      key: "videoSource",
      label: "Video Source",
      renderHeader: () => renderHeader("Video Source"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoSource")}</td>
      ),
    },
    videoOrigName: {
      key: "videoOrigName",
      label: "Original Name",
      renderHeader: () => renderHeader("Original Name"),
      renderCell: (video) => (
        <td
          className="max-w-[200px] truncate px-2 py-3"
          title={video.videoOrigName}
        >
          {extractCellValue(video, "videoOrigName")}
        </td>
      ),
    },
    cameraAngle: {
      key: "cameraAngle",
      label: "Camera Angle",
      renderHeader: () => renderHeader("Camera Angle"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "cameraAngle")}</td>
      ),
    },
    golfClub: {
      key: "golfClub",
      label: "Golf Club",
      renderHeader: () => renderHeader("Golf Club"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "golfClub")}</td>
      ),
    },
    fullName: {
      key: "fullName",
      label: "Full Name",
      renderHeader: () => renderHeader("Full Name"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "fullName")}</td>
      ),
    },
    score: {
      key: "score",
      label: "Score",
      renderHeader: () => renderHeader("Score"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "score")}</td>
      ),
    },
    analyzed: {
      key: "analyzed",
      label: "Analyzed",
      renderHeader: () => renderHeader("Analyzed"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "analyzed")}</td>
      ),
    },
    videoSize: {
      key: "videoSize",
      label: "Size",
      renderHeader: () => renderHeader("Size"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoSize")}</td>
      ),
    },
    videoIsSplit: {
      key: "videoIsSplit",
      label: "Video Is Split",
      renderHeader: () => renderHeader("Video Is Split"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "videoIsSplit")}</td>
      ),
    },
    favorite: {
      key: "favorite",
      label: "Favorite",
      renderHeader: () => renderHeader("Favorite"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "favorite")}</td>
      ),
    },
    goalId: {
      key: "goalId",
      label: "Goal ID",
      renderHeader: () => renderHeader("Goal ID"),
      renderCell: (video) => (
        <td
          className="max-w-[120px] truncate px-2 py-3 font-mono text-sm"
          title={video.goalId}
        >
          {extractCellValue(video, "goalId")}
        </td>
      ),
    },
    analysisPlatform: {
      key: "analysisPlatform",
      label: "Analysis Platform",
      renderHeader: () => renderHeader("Analysis Platform"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "analysisPlatform")}
        </td>
      ),
    },
    tags: {
      key: "tags",
      label: "Tags",
      renderHeader: () => renderHeader("Tags"),
      renderCell: (video) => (
        <td
          className="max-w-[180px] truncate px-2 py-3 text-sm"
          title={formatTags(Array.isArray(video.tags) ? video.tags : [])}
        >
          {extractCellValue(video, "tags")}
        </td>
      ),
    },
    actionIdsCount: {
      key: "actionIdsCount",
      label: "Action IDs Count",
      renderHeader: () => renderHeader("Action IDs Count"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "actionIdsCount")}
        </td>
      ),
    },
    age: {
      key: "age",
      label: "Age",
      renderHeader: () => renderHeader("Age"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "age")}</td>
      ),
    },
    gender: {
      key: "gender",
      label: "Gender",
      renderHeader: () => renderHeader("Gender"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "gender")}</td>
      ),
    },
    hand: {
      key: "hand",
      label: "Hand",
      renderHeader: () => renderHeader("Hand"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "hand")}</td>
      ),
    },
    height: {
      key: "height",
      label: "Height",
      renderHeader: () => renderHeader("Height"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "height")}</td>
      ),
    },
    heightInInches: {
      key: "heightInInches",
      label: "Height (in)",
      renderHeader: () => renderHeader("Height (in)"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "heightInInches")}
        </td>
      ),
    },
    skillLevel: {
      key: "skillLevel",
      label: "Skill Level",
      renderHeader: () => renderHeader("Skill Level"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "skillLevel")}</td>
      ),
    },
    hipMeasurement: {
      key: "hipMeasurement",
      label: "Hip Measurement",
      renderHeader: () => renderHeader("Hip Measurement"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "hipMeasurement")}
        </td>
      ),
    },
    comment: {
      key: "comment",
      label: "Comment",
      renderHeader: () => renderHeader("Comment"),
      renderCell: (video) => (
        <td
          className="max-w-[160px] truncate px-2 py-3 text-sm"
          title={video.userData?.comment}
        >
          {extractCellValue(video, "comment")}
        </td>
      ),
    },
    resolutions: {
      key: "resolutions",
      label: "Resolutions",
      renderHeader: () => renderHeader("Resolutions"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "resolutions")}</td>
      ),
    },
    fps: {
      key: "fps",
      label: "FPS",
      renderHeader: () => renderHeader("FPS"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "fps")}</td>
      ),
    },
    frameCount: {
      key: "frameCount",
      label: "Frame Count",
      renderHeader: () => renderHeader("Frame Count"),
      renderCell: (video) => (
        <td className="px-2 py-3">{extractCellValue(video, "frameCount")}</td>
      ),
    },
    metaDataName: {
      key: "metaDataName",
      label: "Meta Name",
      renderHeader: () => renderHeader("Meta Name"),
      renderCell: (video) => (
        <td
          className="max-w-[180px] truncate px-2 py-3 text-sm"
          title={video.metaData?.name}
        >
          {extractCellValue(video, "metaDataName")}
        </td>
      ),
    },
    metaDataBrand: {
      key: "metaDataBrand",
      label: "Meta Brand",
      renderHeader: () => renderHeader("Meta Brand"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "metaDataBrand")}
        </td>
      ),
    },
    metaDataModel: {
      key: "metaDataModel",
      label: "Meta Model",
      renderHeader: () => renderHeader("Meta Model"),
      renderCell: (video) => (
        <td className="px-2 py-3">
          {extractCellValue(video, "metaDataModel")}
        </td>
      ),
    },
  };
}
